/**
 * 業務詳細 ・ データの流れで、画面から外した情報を要素そのものの `title`（ホバー）へ寄せるための文の組み立て。
 * 見出しの業務名に 未翻訳 ・ アクター ・ ドメインを、業務フロー名にステージと型を束ねる
 * （Dhik 2026-09-29「本当に出すべき情報を 1 or 2 個にするぐらい情報を絞ってほしい」）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面から外した業務の付帯情報を、要素のホバーに出す 1 つの文へ組み立てる。
 * @graph-connects displayLabel [calls] 表示名の空欄の穴埋め
 */
import type { ElementRef } from '@cortex/domain-spec-console';

import { displayLabel, type LabelTranslate } from '../components/spec/UntranslatedBadge.js';

/**
 * 要素への参照を、ホバーに載せる文字列にする。参照が無ければ「—」。
 *
 * @param ref 要素への参照
 * @param translate `useTranslation` の `t`
 * @returns 表示名。参照が無ければ「—」
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要素への参照を表示名の文字列へ直し、無ければ「—」にする。
 * @graph-connects displayLabel [calls] 表示名の空欄の穴埋め
 */
export function refHoverText(
  ref: ElementRef | null | undefined,
  translate: LabelTranslate,
): string {
  if (ref === null || ref === undefined) return '—';
  return displayLabel(ref.label, translate, ref.untranslated);
}

/**
 * 名前のある表示名が未翻訳のときだけ「未翻訳」を返す（札「未翻訳」をホバーへ移した分）。
 * 表示名が空なら本文がすでに「未翻訳」と言っているので返さない（`SpecLabelBadges` と同じ当て方）。
 *
 * @param label 選んだ言語へ畳んだ表示名
 * @param untranslated 選んだ言語に訳が入っていないか
 * @param translate `useTranslation` の `t`
 * @returns 「未翻訳」か undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 名前のある表示名が未翻訳のときだけ、ホバーに載せる「未翻訳」の語を返す。
 * @graph-connects none
 */
export function untranslatedHoverText(
  label: string | null,
  untranslated: boolean,
  translate: LabelTranslate,
): string | undefined {
  return (label ?? '') !== '' && untranslated ? translate('spec.untranslated') : undefined;
}

/**
 * ホバーの行を改行で繋ぐ。空の行は捨て、1 行も残らなければ undefined（title を付けない）。
 *
 * @param lines ホバーに載せる行
 * @returns 改行で繋いだ文。行が無ければ undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ホバーに載せる行を空の行を除いて改行で繋ぐ。
 * @graph-connects none
 */
export function joinHoverLines(lines: readonly (string | undefined)[]): string | undefined {
  const kept = lines.filter((line): line is string => line !== undefined && line !== '');
  return kept.length === 0 ? undefined : kept.join('\n');
}
